/**
 * useImageDragDrop - Manages drag & drop state and handlers for cross-container DnD.
 */

import { useState, useCallback } from 'react';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type {
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
  UniqueIdentifier,
  CollisionDetection,
} from '@dnd-kit/core';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import type { ProductAsset } from '@/types/products';
import type { DragItemData, PendingCopy, PendingUpload } from './types';

interface UseImageDragDropParams {
  getOrderedAssets: (channelKey: string) => ProductAsset[];
  groupedByChannel: Record<string, ProductAsset[]>;
  pendingCopies: PendingCopy[];
  pendingUploads: PendingUpload[];
  handleReorder: (channelKey: string, oldIndex: number, newIndex: number) => void;
  setPendingCopies: React.Dispatch<React.SetStateAction<PendingCopy[]>>;
  setPendingUploads: React.Dispatch<React.SetStateAction<PendingUpload[]>>;
  setChangedChannelKeys: (fn: (prev: Set<string>) => Set<string>) => void;
}

export function useImageDragDrop({
  getOrderedAssets,
  groupedByChannel,
  pendingCopies,
  pendingUploads,
  handleReorder,
  setPendingCopies,
  setPendingUploads,
  setChangedChannelKeys,
}: UseImageDragDropParams) {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeDragData, setActiveDragData] = useState<DragItemData | null>(null);
  const [overChannelKey, setOverChannelKey] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id);
    const data = event.active.data.current as DragItemData | undefined;
    if (data) setActiveDragData(data);
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { over } = event;
    if (!over) { setOverChannelKey(null); return; }
    const overData = over.data.current;
    if (overData?.type === 'channel') {
      setOverChannelKey(overData.channelKey as string);
    } else if (overData?.channelKey) {
      setOverChannelKey(overData.channelKey as string);
    }
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);
      setActiveDragData(null);
      setOverChannelKey(null);

      if (!over || !activeDragData) return;

      const { channelKey: sourceChannelKey, channelId: sourceChannelId, asset: sourceAsset } = activeDragData;
      const overData = over.data.current as { channelKey?: string; channelId?: string | null; type?: string; isEmpty?: boolean } | undefined;
      const targetChannelKey = overData?.type === 'channel' ? overData.channelKey : overData?.channelKey;
      const targetChannelId = overData?.type === 'channel' ? overData.channelId : overData?.channelId;

      // Drag-Verbot: Channel → Global ("Alle Kanäle")
      const isSourceChannel = sourceChannelKey !== 'global' && sourceChannelKey !== '1-1-1';
      const isTargetGlobal = targetChannelKey === 'global' || targetChannelKey === '1-1-1';
      if (isSourceChannel && isTargetGlobal) {
        toast.warning(t('productImages.channelToGlobalForbidden'));
        return;
      }

      if (targetChannelKey === sourceChannelKey && !activeDragData.fromLibrary) {
        // Drop on empty slot: reorder to that slot position
        const overEmpty = overData as { isEmpty?: boolean; slot?: number } | undefined;
        if (overEmpty?.isEmpty && overEmpty.slot !== undefined) {
          const assets = getOrderedAssets(sourceChannelKey);
          const activeLinkId = extractLinkId(active.id);
          const oIdx = assets.findIndex((a) => a.link_id === activeLinkId);
          if (oIdx !== -1) {
            const emptySlot = overEmpty.slot;
            let targetIdx = assets.findIndex((a) => (a.slot ?? a.sort_number) > emptySlot);
            if (targetIdx === -1) targetIdx = assets.length - 1;
            else if (targetIdx > oIdx) targetIdx = Math.max(targetIdx - 1, 0);
            handleReorder(sourceChannelKey, oIdx, targetIdx);
          }
          return;
        }
        handleSameChannelDrop(
          active.id, over.id, sourceChannelKey, getOrderedAssets,
          handleReorder, setPendingUploads, setPendingCopies,
        );
        return;
      }
      if (activeDragData.fromLibrary && targetChannelKey === 'library') return;

      if (targetChannelKey && targetChannelId) {
        handleCrossChannelDrop({
          sourceAsset, sourceChannelId, sourceChannelKey,
          targetChannelKey, targetChannelId,
          overData, overId: over.id, groupedByChannel, pendingCopies, pendingUploads,
          getOrderedAssets, setPendingCopies, setPendingUploads, setChangedChannelKeys,
          isFromLibrary: !!activeDragData.fromLibrary,
        });
      }
    },
    [activeDragData, groupedByChannel, pendingCopies, pendingUploads, getOrderedAssets, handleReorder, setPendingCopies, setPendingUploads, setChangedChannelKeys, t]
  );

  const collisionDetection: CollisionDetection = useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    return pointerCollisions.length > 0 ? pointerCollisions : rectIntersection(args);
  }, []);

  return { activeId, activeDragData, overChannelKey, sensors, handleDragStart, handleDragOver, handleDragEnd, collisionDetection };
}

function isPendingItem(a: ProductAsset): boolean {
  return a.is_pending === true || a.link_id.startsWith('pending-');
}

function extractLinkId(sortableId: UniqueIdentifier): string {
  const s = String(sortableId);
  const sep = s.indexOf('::');
  return sep >= 0 ? s.slice(sep + 2) : s;
}

function handleSameChannelDrop(
  activeId: UniqueIdentifier, overId: UniqueIdentifier, channelKey: string,
  getOrderedAssets: (key: string) => ProductAsset[],
  handleReorder: (key: string, o: number, n: number) => void,
  setPendingUploads: React.Dispatch<React.SetStateAction<PendingUpload[]>>,
  setPendingCopies: React.Dispatch<React.SetStateAction<PendingCopy[]>>,
) {
  const assets = getOrderedAssets(channelKey);
  // Sortable IDs sind channelKey::link_id — extrahiere link_id für Asset-Lookup
  const activeLinkId = extractLinkId(activeId);
  const overLinkId = extractLinkId(overId);
  const oIdx = assets.findIndex((a) => a.link_id === activeLinkId);
  const nIdx = assets.findIndex((a) => a.link_id === overLinkId);
  if (oIdx === -1 || nIdx === -1 || oIdx === nIdx) return;

  const moved = assets[oIdx];
  if (isPendingItem(moved)) {
    reorderPendingItem(assets, moved, oIdx, nIdx, channelKey, setPendingUploads, setPendingCopies);
    return;
  }

  const np = assets.filter((a) => !isPendingItem(a));
  if (np.length === assets.length) { handleReorder(channelKey, oIdx, nIdx); return; }
  const rO = np.findIndex((a) => a.link_id === activeLinkId);
  const tgt = assets[nIdx];
  const rN = isPendingItem(tgt)
    ? assets.slice(0, nIdx).filter((a) => !isPendingItem(a)).length
    : np.findIndex((a) => a.link_id === overLinkId);
  if (rO !== -1 && rN !== -1 && rO !== rN) handleReorder(channelKey, rO, rN);
}

function reorderPendingItem(
  assets: ProductAsset[], moved: ProductAsset, oIdx: number, nIdx: number,
  channelKey: string,
  setPendingUploads: React.Dispatch<React.SetStateAction<PendingUpload[]>>,
  setPendingCopies: React.Dispatch<React.SetStateAction<PendingCopy[]>>,
) {
  const tgt = assets[nIdx];
  const np = assets.filter((a) => !isPendingItem(a));
  const insertAt = isPendingItem(tgt)
    ? assets.slice(0, nIdx).filter((a) => !isPendingItem(a)).length
    : np.findIndex((a) => a.link_id === tgt.link_id) + (nIdx > oIdx ? 1 : 0);

  if (moved.link_id.startsWith('pending-upload-')) {
    setPendingUploads((p) => p.map((u) =>
      u.assetId === moved.asset_id && u.channelKey === channelKey ? { ...u, insertAtIndex: insertAt } : u));
  } else {
    setPendingCopies((p) => p.map((c) =>
      `pending-copy-${c.sourceAssetId}-${c.targetChannelId}` === moved.link_id ? { ...c, insertAtIndex: insertAt } : c));
  }
}

function handleCrossChannelDrop(params: {
  sourceAsset: ProductAsset;
  sourceChannelId: string | null;
  sourceChannelKey: string;
  targetChannelKey: string;
  targetChannelId: string;
  overData: { type?: string } | undefined;
  overId: UniqueIdentifier;
  groupedByChannel: Record<string, ProductAsset[]>;
  pendingCopies: PendingCopy[];
  pendingUploads: PendingUpload[];
  getOrderedAssets: (key: string) => ProductAsset[];
  setPendingCopies: React.Dispatch<React.SetStateAction<PendingCopy[]>>;
  setPendingUploads: React.Dispatch<React.SetStateAction<PendingUpload[]>>;
  setChangedChannelKeys: (fn: (prev: Set<string>) => Set<string>) => void;
  isFromLibrary: boolean;
}) {
  const { sourceAsset, sourceChannelId, sourceChannelKey, targetChannelKey, targetChannelId, overData, overId } = params;
  const targetAssets = params.groupedByChannel[targetChannelKey] || [];
  const alreadyInChannel = targetAssets.some((a) => a.asset_id === sourceAsset.asset_id);
  const alreadyPendingCopy = params.pendingCopies.some(
    (p) => p.sourceAssetId === sourceAsset.asset_id && p.targetChannelId === targetChannelId
  );
  const alreadyPendingUpload = params.pendingUploads.some(
    (p) => p.assetId === sourceAsset.asset_id && p.channelKey === targetChannelKey
  );

  if (alreadyInChannel || alreadyPendingCopy || alreadyPendingUpload) {
    toast.warning(`Dieses Bild ist bereits im Kanal "${targetChannelKey}" vorhanden`);
    return;
  }

  let insertAtIndex: number | undefined;
  if (overData?.type !== 'channel' && overId) {
    const overEmpty = overData as { isEmpty?: boolean; slot?: number } | undefined;
    if (overEmpty?.isEmpty && overEmpty.slot !== undefined) {
      // Dropped onto an empty slot — insert at the slot's position
      const ordered = params.getOrderedAssets(targetChannelKey);
      const slotNum = overEmpty.slot;
      const idx = ordered.findIndex((a) => (a.slot ?? a.sort_number ?? 0) > slotNum);
      insertAtIndex = idx !== -1 ? idx : ordered.length;
    } else {
      const ordered = params.getOrderedAssets(targetChannelKey);
      const overLinkId = extractLinkId(overId);
      const idx = ordered.findIndex((a) => a.link_id === overLinkId);
      if (idx !== -1) insertAtIndex = idx;
    }
  }

  const isPendingUpload = sourceAsset.is_pending || sourceAsset.link_id?.startsWith('pending-upload-');
  if (isPendingUpload || params.isFromLibrary) {
    const orig = params.pendingUploads.find((p) => p.assetId === sourceAsset.asset_id);
    params.setPendingUploads((prev) => {
      // Remove from source channel when moving a pending item between channels
      const filtered = isPendingUpload && !params.isFromLibrary
        ? prev.filter((p) => !(p.assetId === sourceAsset.asset_id && p.channelKey === sourceChannelKey))
        : prev;
      return [...filtered, {
        assetId: sourceAsset.asset_id,
        channelId: targetChannelId,
        channelKey: targetChannelKey,
        width: orig?.width || sourceAsset.width || 0,
        height: orig?.height || sourceAsset.height || 0,
        insertAtIndex,
      }];
    });
  } else {
    params.setPendingCopies((prev) => [...prev, {
      sourceAssetId: sourceAsset.asset_id,
      sourceChannelId: sourceChannelId,
      targetChannelId,
      targetChannelKey,
      asset: sourceAsset,
      insertAtIndex,
    }]);
  }

  params.setChangedChannelKeys((prev) => new Set(prev).add(targetChannelKey));
  toast.info(`Bild zu "${targetChannelKey}" hinzugefügt – Speichern zum Übernehmen`);
}
